import React from "react";
import type { HeroMediaShowcaseBlock } from "../../../types/content";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { useLocalizedTexts, t } from "@/utils/translations";
import { imageSizeAttributes } from "@/utils/imageSize";
import { LCP_IMAGE_PRIORITY, responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./HeroMediaShowcase.module.css";

export interface HeroMediaShowcaseProps {
  /**
   * The hero media showcase block data from Umbraco
   */
  block: HeroMediaShowcaseBlock;

  /**
   * Heading level to use (default: h1)
   */
  headingLevel?: "h1" | "h2";

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * Hero Media Showcase component
 * Split-layout hero section with headline, body text, CTA, and media showcase
 */
export const HeroMediaShowcase: React.FC<HeroMediaShowcaseProps> = ({
  block,
  headingLevel = "h1",
  className,
}) => {
  const localizedTexts = useLocalizedTexts();
  const {
    heading,
    bodyText,
    ctaButton,
    backgroundImage,
    backgroundColor,
    mediaType = "image",
    backgroundVideo,
  } = block;

  const HeadingTag = headingLevel;

  // Parse heading for line breaks
  const headingLines = heading.split("\\n").filter(Boolean);

  const containerClasses = [styles.hero, className].filter(Boolean).join(" ");

  const leftPanelStyle: React.CSSProperties = backgroundColor
    ? { backgroundColor }
    : {};

  return (
    <section className={containerClasses} data-component="hero-media-showcase">
      {/* Left Panel - Text Content */}
      <div className={styles.leftPanel} style={leftPanelStyle}>
        <HeadingTag className={styles.heading}>
          {headingLines.map((line, index) => (
            <React.Fragment key={index}>
              {line}
              {index < headingLines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </HeadingTag>

        <p className={styles.bodyText}>{bodyText}</p>

        {ctaButton && (
          <a
            href={resolveHref(ctaButton, getLocale().language)}
            target={ctaButton.target || "_self"}
            className={styles.ctaButton}
            rel={
              ctaButton.target === "_blank" ? "noopener noreferrer" : undefined
            }
          >
            {ctaButton.name ||
              t(localizedTexts, "Tracker.Generic.LearnMore", "Learn more")}
          </a>
        )}
      </div>

      {/* Right Panel - Hero Media */}
      <div className={styles.rightPanel}>
        {mediaType === "video" && backgroundVideo ? (
          <video
            ref={(el) => {
              if (el) {
                el.muted = true;
                el.defaultMuted = true;
                el.play().catch(() => {});
              }
            }}
            autoPlay
            loop
            muted
            playsInline
            poster={backgroundImage?.url ?? undefined}
            className={styles.backgroundVideo}
            src={backgroundVideo.url}
          >
            <track kind="captions" />
          </video>
        ) : backgroundImage ? (
          <img
            {...responsiveCmsImage(
              backgroundImage.url,
              "(max-width: 768px) 100vw, 50vw",
              backgroundImage.width
            )}
            alt={backgroundImage.altText || ""}
            className={styles.backgroundImage}
            loading="eager"
            {...LCP_IMAGE_PRIORITY}
            {...imageSizeAttributes(backgroundImage)}
          />
        ) : null}
      </div>
    </section>
  );
};

export default HeroMediaShowcase;
